import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { backendImageService } from '../../services/backendImageService';
import { partnerService } from '../../services/partnerService';
import { teamMemberService } from '../../services/teamMemberService';
import ImageUpload from '../Common/ImageUpload';
import type { Partner, TeamMember } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { API_BASE_URL } from '../../config/apiConfig';

const API_BASE = API_BASE_URL.replace(/\/api$/, '');

/** Resolves any image URL (Cloudinary, local, or missing) to a displayable URL */
const resolveImg = (url?: string | null): string => {
  if (!url || url === '') return '';
  // If it's already a full URL (Cloudinary or absolute), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Connect protocol-less cloudinary links to https
  if (url.includes('res.cloudinary.com')) {
    return url.startsWith('http') ? url : `https://${url.replace(/^\/+/, '')}`;
  }
  // If it's a relative path, prepend backend base
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
};

const Avatar = ({
  src, name, size = 'md', rounded = 'full'
}: { src?: string | null; name: string; size?: 'sm' | 'md' | 'lg'; rounded?: 'full' | 'xl' }) => {
  const [imgSrc, setImgSrc] = useState(resolveImg(src));
  
  // Update imgSrc if src prop changes
  useEffect(() => {
    setImgSrc(resolveImg(src));
  }, [src]);

  const sizeMap = { sm: 'w-10 h-10 text-sm', md: 'w-20 h-20 text-xl', lg: 'w-28 h-28 text-3xl' };
  const cls = `${sizeMap[size]} rounded-${rounded} overflow-hidden bg-primary-100 flex items-center justify-center flex-shrink-0`;
  const initials = name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className={cls}>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgSrc('')}
        />
      ) : (
        <span className={`font-bold text-primary-700`}>{initials}</span>
      )}
    </div>
  );
};

interface ModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  children: React.ReactNode;
  submitLabel: string;
  dark?: boolean;
}

const Modal: React.FC<ModalProps> = ({ title, onClose, onSubmit, children, submitLabel, dark }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden animate-in fade-in duration-300">
    <div 
      className={`w-full sm:max-w-2xl ${dark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'} 
      rounded-t-[2.5rem] sm:rounded-3xl shadow-2xl flex flex-col 
      h-[92vh] sm:h-auto sm:max-h-[90vh] overflow-hidden 
      animate-in slide-in-from-bottom sm:slide-in-from-bottom-8 duration-500`}
    >
      {/* Drag handle for mobile visual hint */}
      <div className="sm:hidden w-12 h-1.5 bg-slate-400/20 mx-auto mt-4 rounded-full flex-shrink-0" />
      
      {/* Modal header */}
      <div className="flex items-center justify-between px-6 py-5 sm:py-6 sm:rounded-t-3xl flex-shrink-0">
        <h3 className="text-xl font-extrabold tracking-tight">{title}</h3>
        <button 
          onClick={onClose} 
          className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:scale-110 active:scale-95 flex items-center justify-center transition-all"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-2 scroll-smooth scrollbar-none">
        <form id="about-form" onSubmit={onSubmit} className="space-y-6 pb-10">
          {children}
        </form>
      </div>

      {/* Footer */}
      <div className={`flex flex-col sm:flex-row gap-3 px-6 py-6 border-t flex-shrink-0 ${dark ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'} backdrop-blur-md`}>
        <button 
          type="button" 
          onClick={onClose} 
          className={`order-2 sm:order-1 flex-1 sm:flex-none px-6 py-3.5 rounded-2xl border text-sm font-bold transition-all active:scale-95 ${dark ? 'border-slate-700 text-slate-400 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          form="about-form" 
          className="order-1 sm:order-2 flex-1 sm:flex-none px-8 py-3.5 rounded-2xl bg-primary-700 text-white text-sm font-black hover:bg-primary-800 transition-all shadow-lg shadow-primary-700/20 active:scale-95"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── Team Member Form Data ───────────────────────────────────────────────────

const defaultTeamForm = {
  name: '', role: '', bio: '', imageUrl: '', specialties: [] as string[],
  email: '', phone: '', linkedinUrl: '', isFounder: false, sortOrder: 0, active: true, featured: false,
};

const defaultPartnerForm = {
  name: '', type: 'conservancy', description: '', location: '', website: '',
  contactEmail: '', contactPhone: '', impact: '', imageUrl: '', logoUrl: '',
  active: true, featured: false, sortOrder: 0,
};

type Section = 'team' | 'partners';

const AboutManagement: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { actualTheme } = useTheme();
  const dark = actualTheme === 'dark';

  // ─── Styling helpers ──────────────────────────────────────────────────────
  // ─── Styling helpers ──────────────────────────────────────────────────────
  const cardCls = `rounded-3xl border transition-all duration-300 ${dark ? 'bg-slate-800 border-slate-700/50' : 'bg-white border-slate-200/60'} shadow-sm hover:shadow-md`;
  const inputCls = `w-full px-4 py-3.5 rounded-2xl border text-base sm:text-sm transition-all focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 ${dark ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-200 text-gray-900 placeholder-slate-400'}`;
  const labelCls = `block text-xs font-bold uppercase tracking-[0.15em] mb-2 ${dark ? 'text-slate-500' : 'text-slate-400'}`;
  const tagCls = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${dark ? 'bg-primary-900/40 text-primary-300 border border-primary-800' : 'bg-primary-50 text-primary-700 border border-primary-100'}`;

  const [section, setSection] = useState<Section>('team');
  const [loading, setLoading] = useState(true);

  // Team state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [teamForm, setTeamForm] = useState(defaultTeamForm);
  const [teamSpecialtyInput, setTeamSpecialtyInput] = useState('');

  // Partner state
  const [partners, setPartners] = useState<Partner[]>([]);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [partnerForm, setPartnerForm] = useState(defaultPartnerForm);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (section === 'team') {
        const data = await teamMemberService.getAllTeamMembers();
        setTeamMembers(data);
      } else {
        const data = await partnerService.getAllPartners(true); // force fresh
        setPartners(data);
      }
    } catch {
      showError('Failed to load data', 'Error');
    } finally {
      setLoading(false);
    }
  }, [section, showError]);

  useEffect(() => { loadData(); }, [loadData]);

  // ESC key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setShowTeamForm(false); setShowPartnerForm(false); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // TEAM handlers
  const openAddTeam = () => { setEditingMember(null); setTeamForm(defaultTeamForm); setShowTeamForm(true); };
  const openEditTeam = (m: TeamMember) => {
    setEditingMember(m);
    setTeamForm({
      name: m.name, role: m.role, bio: m.bio, imageUrl: m.imageUrl || '',
      specialties: m.specialties, email: m.email || '', phone: m.phone || '',
      linkedinUrl: m.linkedinUrl || '', isFounder: m.isFounder, sortOrder: m.sortOrder,
      active: m.active, featured: m.featured,
    });
    setShowTeamForm(true);
  };

  const handleTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await teamMemberService.updateTeamMember(editingMember.id, teamForm as any);
        showSuccess('Team member updated!', 'Updated');
      } else {
        await teamMemberService.addTeamMember(teamForm as any);
        showSuccess('Team member added!', 'Added');
      }
      setShowTeamForm(false);
      setEditingMember(null);
      loadData();
    } catch { showError('Failed to save team member', 'Error'); }
  };

  const handleTeamDelete = async (m: TeamMember) => {
    if (m.isFounder) { showError('Cannot delete the founder', 'Permission Denied'); return; }
    if (!window.confirm(`Delete ${m.name}?`)) return;
    try { await teamMemberService.deleteTeamMember(m.id); showSuccess('Deleted', 'Deleted'); loadData(); }
    catch { showError('Failed to delete', 'Error'); }
  };

  const addSpecialty = () => {
    const val = teamSpecialtyInput.trim();
    if (!val) return;
    if (teamForm.specialties.includes(val)) {
      setTeamSpecialtyInput('');
      return;
    }
    setTeamForm(p => ({ ...p, specialties: [...p.specialties, val] }));
    setTeamSpecialtyInput('');
  };

  const removeSpecialty = (val: string) => {
    setTeamForm(p => ({ ...p, specialties: p.specialties.filter(s => s !== val) }));
  };

  // PARTNER handlers
  const openAddPartner = () => { setEditingPartner(null); setPartnerForm(defaultPartnerForm as any); setShowPartnerForm(true); };
  const openEditPartner = (p: Partner) => {
    setEditingPartner(p);
    setPartnerForm({
      name: p.name, type: p.type, description: p.description, location: p.location,
      website: p.website || '', contactEmail: p.contactEmail || '', contactPhone: p.contactPhone || '',
      impact: p.impact, imageUrl: p.imageUrl || '', logoUrl: p.logoUrl || '',
      active: p.active, featured: p.featured, sortOrder: p.sortOrder,
    });
    setShowPartnerForm(true);
  };

  const handlePartnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await partnerService.updatePartner(editingPartner.id, partnerForm as any);
        showSuccess('Partner updated!', 'Updated');
      } else {
        await partnerService.addPartner(partnerForm as any);
        showSuccess('Partner added!', 'Added');
      }
      setShowPartnerForm(false);
      setEditingPartner(null);
      loadData();
    } catch { showError('Failed to save partner', 'Error'); }
  };

  const handlePartnerDelete = async (p: Partner) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await partnerService.deletePartner(p.id); showSuccess('Deleted', 'Deleted'); loadData(); }
    catch { showError('Failed to delete', 'Error'); }
  };



  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className={`text-2xl font-bold ${dark ? 'text-slate-100' : 'text-gray-900'}`}>About Page</h2>
          <p className={`text-sm mt-0.5 ${dark ? 'text-slate-400' : 'text-gray-500'}`}>Manage team members and conservation partners</p>
        </div>
        <div className="flex items-center gap-3">
          {section === 'team' && (
            <a
              href="/business-card.html"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-colors shadow-sm ${dark ? 'border-primary-500/30 text-primary-400 hover:bg-primary-500/10' : 'border-primary-200 text-primary-700 hover:bg-primary-50'}`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Card Generator
            </a>
          )}
          <button
            onClick={section === 'team' ? openAddTeam : openAddPartner}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-700 text-white text-sm font-bold hover:bg-primary-800 transition-colors shadow-md"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {section === 'team' ? 'Add Team Member' : 'Add Partner'}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className={`inline-flex rounded-xl p-1 gap-1 ${dark ? 'bg-slate-800' : 'bg-gray-100'}`}>
        {(['team', 'partners'] as Section[]).map(s => (
          <button key={s} onClick={() => setSection(s)}
            className={`px-6 py-2.5 rounded-lg text-sm font-semibold capitalize transition-all ${section === s ? 'bg-primary-700 text-white shadow-md' : dark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-900'}`}>
            {s === 'team' ? '👥 Team Members' : '🤝 Partners'}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-12 h-12 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
          <p className={`text-sm ${dark ? 'text-slate-400' : 'text-gray-500'}`}>Loading...</p>
        </div>
      ) : section === 'team' ? (
        teamMembers.length === 0 ? (
          <div className={`text-center py-20 ${cardCls}`}>
            <div className="text-5xl mb-4">👤</div>
            <h3 className={`text-lg font-bold mb-2 ${dark ? 'text-slate-200' : 'text-gray-900'}`}>No team members yet</h3>
            <button onClick={openAddTeam} className="mt-4 px-6 py-2.5 bg-primary-700 text-white rounded-xl font-semibold text-sm hover:bg-primary-800 transition-colors">
              Add First Team Member
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {teamMembers.map(m => (
              <div key={m.id} className={`${cardCls} overflow-hidden hover:shadow-md transition-shadow ${!m.active ? 'opacity-60' : ''}`}>
                <div className="bg-gradient-to-br from-primary-800 to-emerald-950 px-5 pt-6 pb-10 flex flex-col items-center gap-3">
                  <Avatar src={m.imageUrl} name={m.name} size="lg" />
                  {m.isFounder && (
                    <span className="px-3 py-1 bg-amber-400/20 text-amber-300 rounded-full text-xs font-bold border border-amber-400/30 tracking-wide">
                      ★ Founder
                    </span>
                  )}
                </div>
                <div className="-mt-6 mx-4 relative">
                  <div className={`rounded-2xl shadow-md px-4 py-3 mb-4 text-center ${dark ? 'bg-slate-700' : 'bg-white'} border ${dark ? 'border-slate-600' : 'border-gray-100'}`}>
                    <h3 className={`font-bold text-base ${dark ? 'text-slate-100' : 'text-gray-900'}`}>{m.name}</h3>
                    <p className={`text-sm font-medium text-primary-600 mt-0.5`}>{m.role}</p>
                    {m.email && <p className={`text-xs mt-1 truncate ${dark ? 'text-slate-400' : 'text-gray-500'}`}>{m.email}</p>}
                  </div>
                </div>
                {m.specialties?.length > 0 && (
                  <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                    {m.specialties.slice(0, 3).map(s => (
                      <span key={s} className="bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full text-xs font-medium">{s}</span>
                    ))}
                  </div>
                )}
                <div className={`flex justify-end gap-2 px-4 pb-4 pt-2 border-t ${dark ? 'border-slate-700' : 'border-gray-100'}`}>
                  <button onClick={() => openEditTeam(m)} className={`p-2 rounded-xl transition-colors ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} title="Edit">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  {!m.isFounder && (
                    <button onClick={() => handleTeamDelete(m)} className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 transition-colors" title="Delete">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // Partners section
        partners.length === 0 ? (
          <div className={`text-center py-20 ${cardCls}`}>
            <div className="text-5xl mb-4">🤝</div>
            <h3 className={`text-lg font-bold mb-2 ${dark ? 'text-slate-200' : 'text-gray-900'}`}>No partners yet</h3>
            <button onClick={openAddPartner} className="mt-4 px-6 py-2.5 bg-primary-700 text-white rounded-xl font-semibold text-sm hover:bg-primary-800 transition-colors">
              Add First Partner
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {partners.map(p => (
              <div key={p.id} className={`${cardCls} flex gap-4 p-5 hover:shadow-md transition-shadow ${!p.active ? 'opacity-60' : ''}`}>
                <Avatar src={p.logoUrl || p.imageUrl} name={p.name} size="md" rounded="xl" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className={`font-bold text-base ${dark ? 'text-slate-100' : 'text-gray-900'}`}>{p.name}</h3>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${ p.type === 'conservancy' ? 'bg-green-100 text-green-700' : p.type === 'ngo' ? 'bg-blue-100 text-blue-700' : p.type === 'community' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                        {p.type}
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => openEditPartner(p)} className={`p-1.5 rounded-lg transition-colors ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button onClick={() => handlePartnerDelete(p)} className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                  <p className={`text-sm mt-1.5 line-clamp-2 ${dark ? 'text-slate-400' : 'text-gray-600'}`}>{p.description}</p>
                  {p.location && <p className={`text-xs mt-1 ${dark ? 'text-slate-500' : 'text-gray-400'}`}>📍 {p.location}</p>}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ─── TEAM MEMBER MODAL ─── */}
      {showTeamForm && (
        <Modal
          title={editingMember ? 'Edit Team Member' : 'Add Team Member'}
          onClose={() => { setShowTeamForm(false); setEditingMember(null); }}
          onSubmit={handleTeamSubmit}
          submitLabel={editingMember ? 'Save Changes' : 'Add Member'}
          dark={dark}
        >
          {editingMember?.isFounder && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
              ★ <strong>Founder Profile:</strong> Only the profile image can be changed. Name, role, and bio are protected.
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Photo (Constrained Side Container) */}
            <div className="flex-shrink-0 w-full md:w-48 flex flex-col items-center">
              <label className={`${labelCls} w-full text-center`}>Profile Photo</label>
              <div className="w-40 h-auto overflow-visible rounded-full ring-4 ring-primary-500/10 p-1">
                <ImageUpload
                  onImageUpload={(url) => setTeamForm(p => ({ ...p, imageUrl: url }))}
                  currentImageUrl={teamForm.imageUrl}
                  aspectRatio="square"
                  maxSize={10}
                  category="team"
                  placeholder="Photo"
                  className="w-full"
                />
              </div>
            </div>

            {/* Primary Details */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Name *</label>
                <input type="text" className={inputCls} value={teamForm.name} required
                  disabled={editingMember?.isFounder}
                  onChange={e => setTeamForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Full name" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Role *</label>
                <input type="text" className={inputCls} value={teamForm.role} required
                  disabled={editingMember?.isFounder}
                  onChange={e => setTeamForm(p => ({ ...p, role: e.target.value }))}
                  placeholder="e.g. Founder, Guide" />
              </div>
              
              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 pt-2">
                {[
                  { key: 'active', label: '✅ Active' },
                  { key: 'featured', label: '⭐ Featured' },
                ].map(({ key, label }) => (
                  <label key={key} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-all ${dark ? 'border-slate-600 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                    <input type="checkbox" checked={(teamForm as any)[key]}
                      onChange={e => setTeamForm(p => ({ ...p, [key]: e.target.checked }))}
                      className="w-4 h-4 text-primary-600 rounded" />
                    <span className={`text-sm font-medium ${dark ? 'text-slate-300' : 'text-gray-700'}`}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4 mt-2 ${dark ? 'border-slate-700' : 'border-gray-100'}`}>
            <div className="sm:col-span-2">
              <label className={labelCls}>Bio *</label>
              <textarea className={inputCls} value={teamForm.bio} required rows={3}
                disabled={editingMember?.isFounder}
                onChange={e => setTeamForm(p => ({ ...p, bio: e.target.value }))}
                placeholder="Brief background and expertise" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" className={inputCls} value={teamForm.email}
                onChange={e => setTeamForm(p => ({ ...p, email: e.target.value }))}
                placeholder="member@sighttrails.com" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input type="tel" className={inputCls} value={teamForm.phone}
                onChange={e => setTeamForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+254 7XX XXX XXX" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>LinkedIn URL</label>
              <input type="url" className={inputCls} value={teamForm.linkedinUrl}
                onChange={e => setTeamForm(p => ({ ...p, linkedinUrl: e.target.value }))}
                placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Specialties</label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    className={inputCls}
                    value={teamSpecialtyInput}
                    onChange={e => setTeamSpecialtyInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSpecialty();
                      }
                    }}
                    placeholder="e.g. Wildlife (Press Enter to add)" 
                  />
                  <button
                    type="button"
                    onClick={addSpecialty}
                    className="px-5 py-2.5 rounded-2xl bg-primary-700 text-white text-xs font-black uppercase tracking-widest hover:bg-primary-800 transition-all shadow-lg shadow-primary-700/20 active:scale-95"
                  >
                    Add
                  </button>
                </div>
                
                {teamForm.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-4 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
                    {teamForm.specialties.map(s => (
                      <span key={s} className={tagCls}>
                        {s}
                        <button 
                          type="button" 
                          onClick={() => removeSpecialty(s)}
                          className="hover:text-red-500 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── PARTNER MODAL ─── */}
      {showPartnerForm && (
        <Modal
          title={editingPartner ? 'Edit Partner' : 'Add Partner'}
          onClose={() => { setShowPartnerForm(false); setEditingPartner(null); }}
          onSubmit={handlePartnerSubmit}
          submitLabel={editingPartner ? 'Save Changes' : 'Add Partner'}
          dark={dark}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Partner Name *</label>
              <input type="text" className={inputCls} value={partnerForm.name} required
                onChange={e => setPartnerForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Maasai Mara Conservancy" />
            </div>
            <div>
              <label className={labelCls}>Type *</label>
              <select className={inputCls} value={partnerForm.type}
                onChange={e => setPartnerForm(p => ({ ...p, type: e.target.value }))}>
                <option value="conservancy">Conservancy</option>
                <option value="ngo">NGO</option>
                <option value="community">Community Group</option>
                <option value="research">Research Institution</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Location *</label>
              <input type="text" className={inputCls} value={partnerForm.location} required
                onChange={e => setPartnerForm(p => ({ ...p, location: e.target.value }))}
                placeholder="e.g. Narok, Kenya" />
            </div>
            
            <div className={`sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4 mt-2 ${dark ? 'border-slate-700' : 'border-gray-100'}`}>
              <div className="sm:col-span-2">
                <label className={labelCls}>Website</label>
                <input type="url" className={inputCls} value={partnerForm.website}
                  onChange={e => setPartnerForm(p => ({ ...p, website: e.target.value }))}
                  placeholder="https://..." />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Description *</label>
                <textarea className={inputCls} value={partnerForm.description} required rows={3}
                  onChange={e => setPartnerForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe this partner..." />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Impact *</label>
                <textarea className={inputCls} value={partnerForm.impact} required rows={2}
                  onChange={e => setPartnerForm(p => ({ ...p, impact: e.target.value }))}
                  placeholder="Describe the positive impact..." />
              </div>
              <div>
                <label className={labelCls}>Contact Email</label>
                <input type="email" className={inputCls} value={partnerForm.contactEmail}
                  onChange={e => setPartnerForm(p => ({ ...p, contactEmail: e.target.value }))}
                  placeholder="partner@example.com" />
              </div>
              <div>
                <label className={labelCls}>Contact Phone</label>
                <input type="tel" className={inputCls} value={partnerForm.contactPhone}
                  onChange={e => setPartnerForm(p => ({ ...p, contactPhone: e.target.value }))}
                  placeholder="+254..." />
              </div>
            </div>
          </div>

          <div className={`flex flex-col md:flex-row gap-6 mt-2 border-t pt-4 ${dark ? 'border-slate-700' : 'border-gray-100'}`}>
            {/* Logo upload */}
            <div className="flex-1">
              <label className={`${labelCls} text-center`}>Partner Logo</label>
              <div className="w-28 mx-auto overflow-visible rounded-2xl ring-4 ring-primary-500/10 p-1">
                <ImageUpload
                  onImageUpload={(url) => setPartnerForm(p => ({ ...p, logoUrl: url }))}
                  currentImageUrl={partnerForm.logoUrl}
                  aspectRatio="square" maxSize={10} category="partners"
                  placeholder="Logo" className="w-full"
                />
              </div>
            </div>

            {/* Image upload */}
            <div className="flex-1">
              <label className={`${labelCls} text-center`}>Partner Image</label>
              <div className="w-40 mx-auto overflow-visible rounded-2xl ring-4 ring-primary-500/10 p-1">
                <ImageUpload
                  onImageUpload={(url) => setPartnerForm(p => ({ ...p, imageUrl: url }))}
                  currentImageUrl={partnerForm.imageUrl}
                  aspectRatio="landscape" maxSize={10} category="partners"
                  placeholder="Image" className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {[{ key: 'active', label: '✅ Active' }, { key: 'featured', label: '⭐ Featured' }].map(({ key, label }) => (
              <label key={key} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-all ${dark ? 'border-slate-600 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                <input type="checkbox" checked={(partnerForm as any)[key]}
                  onChange={e => setPartnerForm(p => ({ ...p, [key]: e.target.checked }))}
                  className="w-4 h-4 text-primary-600 rounded" />
                <span className={`text-sm font-medium ${dark ? 'text-slate-300' : 'text-gray-700'}`}>{label}</span>
              </label>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AboutManagement;
